import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { formatCurrency } from '../../utils/formatters';
import { clampDownPaymentPercent, getSaveCardDisclaimer } from '../../utils/downPaymentDisplay';
import { getStripePublishableKey } from '../../config/stripe';

// Get Stripe publishable key from centralized config
// This ensures consistent key management across the app
const STRIPE_PUBLISHABLE_KEY = getStripePublishableKey();

// Load Stripe outside of component to avoid recreating it on render
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

// Styles for Stripe Card Element
const cardElementOptions = {
  style: {
    base: {
      fontFamily: 'Gotham SSm, -apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: '16px',
      color: '#283747',
      '::placeholder': {
        color: '#97a0af',
      },
    },
    invalid: {
      color: '#ff5630',
      iconColor: '#ff5630',
    },
  },
  hidePostalCode: true,
};

interface InitiateBookingResult {
  clientSecret: string;
  setupIntentId: string;
  appointmentId?: string; // Optional in the card-first flow (appointment created after card confirmed)
}

interface PaymentFormProps {
  amount: number;
  /** Pre-existing client secret (optional). If absent, onInitiateBooking is called first. */
  clientSecret?: string | null;
  onPaymentSuccess: (paymentMethodId?: string, setupIntentId?: string, appointmentId?: string) => void | Promise<void>;
  loading: boolean;
  totalServicePrice?: number;
  /** Stylist's resolved down payment percentage (e.g. 50). 0 means off / none. */
  downPaymentPercentage?: number;
  /**
   * 'payment' — charge the card now (confirmCardPayment).
   * 'setup'   — save the card for later (confirmCardSetup, no charge now).
   * Defaults to 'payment'.
   */
  mode?: 'payment' | 'setup';
  /**
   * Called in setup mode when no clientSecret exists yet.
   * Should create the appointment and setup intent and return the result.
   */
  onInitiateBooking?: () => Promise<InitiateBookingResult | null>;
}

// Inner form component that uses Stripe hooks
const PaymentFormContent: React.FC<PaymentFormProps> = ({
  amount,
  clientSecret,
  onPaymentSuccess,
  loading,
  totalServicePrice,
  downPaymentPercentage = 0,
  mode = 'payment',
  onInitiateBooking,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const percent = clampDownPaymentPercent(downPaymentPercentage);
  const actualTotalPrice = totalServicePrice || amount;
  const downPaymentAmount = Math.round(actualTotalPrice * (percent / 100) * 100) / 100;
  const remainingBalance = actualTotalPrice - downPaymentAmount;
  
  // Log Stripe initialization and client secret status
  useEffect(() => {
    console.log('Stripe initialization status:', stripe ? 'Initialized' : 'Not initialized');
    console.log('Client secret status:', clientSecret ? 'Available' : 'Not available');
    console.log('Payment amounts:', {
      total: actualTotalPrice,
      downPayment: downPaymentAmount,
      remaining: remainingBalance,
    });
    
    if (!stripe) {
      console.warn('Stripe has not loaded yet. Check if Stripe key is valid.');
    }
    
    if (!clientSecret) {
      console.warn('Client secret not available. Payment intent may not have been created.');
    }
  }, [stripe, clientSecret, actualTotalPrice, downPaymentAmount, remainingBalance]);
  
  useEffect(() => {
    if (stripe && clientSecret && !paymentComplete && mode === 'payment') {
      const retrievePaymentIntent = async () => {
        try {
          const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
          if (paymentIntent?.status === 'succeeded') {
            setPaymentComplete(true);
            onPaymentSuccess();
          }
        } catch (error) {
          console.error('Error retrieving payment intent:', error);
          setError('Could not verify payment status. Please contact support if payment was completed.');
        }
      };
      retrievePaymentIntent();
    }
  }, [stripe, clientSecret, paymentComplete, onPaymentSuccess, mode]);
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe) {
      setError('Stripe failed to load. Please refresh the page and try again.');
      return;
    }

    if (!elements) {
      setError('Payment elements failed to load. Please refresh the page.');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError('Card element not found. Please refresh the page and try again.');
        setProcessing(false);
        return;
      }

      if (mode === 'setup') {
        // Resolve clientSecret — either pre-supplied or fetched via onInitiateBooking
        let resolvedSecret = clientSecret ?? null;
        let resolvedSetupIntentId: string | undefined;
        let resolvedAppointmentId: string | undefined;

        if (!resolvedSecret) {
          if (!onInitiateBooking) {
            setError('Payment setup incomplete. Please try again or contact support.');
            setProcessing(false);
            return;
          }
          try {
            const result = await onInitiateBooking();
            if (!result) {
              setError('Failed to prepare booking. Please try again.');
              setProcessing(false);
              return;
            }
            resolvedSecret = result.clientSecret;
            resolvedSetupIntentId = result.setupIntentId;
            resolvedAppointmentId = result.appointmentId;
          } catch (initiateErr: any) {
            setError(initiateErr.message || 'Failed to prepare booking. Please try again.');
            setProcessing(false);
            return;
          }
        }

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(resolvedSecret, {
          payment_method: { card: cardElement },
        });

        if (setupError) {
          setError(setupError.message || 'Failed to save card. Please try again.');
          setProcessing(false);
          return;
        }

        if (setupIntent?.status === 'succeeded') {
          const pmId =
            typeof setupIntent.payment_method === 'string'
              ? setupIntent.payment_method
              : (setupIntent.payment_method as any)?.id;
          try {
            await onPaymentSuccess(pmId, resolvedSetupIntentId ?? setupIntent.id, resolvedAppointmentId);
            // Only show success state if the booking was actually created successfully
            setPaymentComplete(true);
          } catch (bookingErr: any) {
            // Card was saved but booking creation failed — show the error without a success message
            const msg =
              bookingErr?.response?.data?.message ||
              bookingErr?.message ||
              'Your card was saved but the booking could not be completed. Please try again.';
            setError(msg);
            setProcessing(false);
          }
        } else {
          setError('Card setup was not completed. Please try again.');
          setProcessing(false);
        }
        return;
      }

      // Payment Intent flow: charge the card now
      if (!clientSecret) {
        setError('Payment setup incomplete. Please try again or contact support.');
        setProcessing(false);
        return;
      }

      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (paymentError) {
        setError(paymentError.message || 'An error occurred during payment');
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        setPaymentComplete(true);
        onPaymentSuccess();
      } else if (paymentIntent.status === 'requires_action') {
        setError('This payment requires additional verification. Please follow the prompts.');
        setProcessing(false);
      } else if (paymentIntent.status === 'processing') {
        setError("Your payment is processing. We'll update you when it completes.");
        setProcessing(false);
      } else {
        setError(`Payment status: ${paymentIntent.status}. Please contact support if this persists.`);
        setProcessing(false);
      }
    } catch (error: any) {
      console.error('Unexpected payment error:', error);
      setError('An unexpected error occurred. Please try again or use a different payment method.');
      setProcessing(false);
    }
  };
  
  return (
    <div className="payment-card">
      <h3 className="payment-card__title">
        {mode === 'setup' ? 'Save Card Details' : 'Payment Details'}
      </h3>
      
      {error && (
        <div className="message message--error">
          {error}
        </div>
      )}
      
      {paymentComplete ? (
        <div className="message message--success">
          {mode === 'setup'
            ? 'Card saved! Your booking is confirmed.'
            : 'Payment successful! Your appointment has been booked.'}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="payment-form">
          <div className="payment-form__field">
            <label className="payment-form__label">
              Card Information
            </label>
            <div className="payment-form__card-element">
              <CardElement options={cardElementOptions} />
            </div>
          </div>
          
          <div className="payment-form__divider"></div>
          
          {/* Display price breakdown */}
          <div className="payment-form__pricing">
            <div className="payment-form__price-row">
              <div className="payment-form__price-label">Service Total:</div>
              <div className="payment-form__price-value">{formatCurrency(actualTotalPrice)}</div>
            </div>
            {percent > 0 ? (
              <>
                <div className="payment-form__price-row payment-form__price-row--highlight">
                  <div className="payment-form__price-label">Quoted booking guarantee ({percent}%):</div>
                  <div className="payment-form__price-value">{formatCurrency(downPaymentAmount)}</div>
                </div>
                <div className="payment-form__price-row payment-form__price-row--muted">
                  <div className="payment-form__price-label">Charged at completion:</div>
                  <div className="payment-form__price-value">{formatCurrency(actualTotalPrice)}</div>
                </div>
              </>
            ) : (
              <div className="payment-form__price-row payment-form__price-row--muted">
                <div className="payment-form__price-label">Due at Completion:</div>
                <div className="payment-form__price-value">{formatCurrency(actualTotalPrice)}</div>
              </div>
            )}
          </div>
          
          <div className="payment-form__divider"></div>
          
          <div className="payment-form__total">
            <div className="payment-form__total-label">
              {mode === 'setup' ? 'Due Now:' : percent > 0 ? 'Amount Due Now:' : 'Due at Completion:'}
            </div>
            <div className="payment-form__total-amount">
              {mode === 'setup'
                ? formatCurrency(0)
                : formatCurrency(percent > 0 ? downPaymentAmount : actualTotalPrice)}
            </div>
          </div>
          
          <div className="payment-form__note">
            <small>
              {mode === 'setup'
                ? getSaveCardDisclaimer(percent, formatCurrency(downPaymentAmount))
                : percent > 0
                  ? `A ${percent}% booking guarantee is quoted and charged only if you cancel late or no-show. The full amount is collected when your appointment is completed.`
                  : 'No deposit now. The full amount will be charged when your appointment is completed.'}
            </small>
          </div>
          
          <button
            type="submit"
            className="payment-form__submit-button"
            disabled={!stripe || processing || loading || (mode === 'payment' && !clientSecret)}
          >
            {processing || loading
              ? 'Processing...'
              : mode === 'setup'
                ? 'Save Card & Confirm Booking'
                : percent > 0
                  ? `Pay Booking Guarantee ${formatCurrency(downPaymentAmount)}`
                  : 'Confirm Booking'}
          </button>
          
          <div className="payment-form__security">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="payment-form__security-icon">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
            Secure payment processed by Stripe
          </div>
        </form>
      )}
    </div>
  );
};

// Wrapper component that provides Stripe Elements
const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent {...props} />
    </Elements>
  );
};

export default PaymentForm;